import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import Header from "../Header/header";
import Fotter from "../Fotter/fotter";
import API_URL from "../../config";

import {
    Calendar,
    Clock,
    Tag,
    ChevronLeft,
    Share2,
    Bookmark,
    MessageSquare,
    Sparkles,
    ArrowRight,
    TrendingUp,
    Loader2,
    Compass
} from "lucide-react";

const BlogDetail = () => {
    const { id } = useParams();
    const [blog, setBlog] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        const fetchBlog = async () => {
            try {
                setIsLoading(true);
                const response = await axios.get(`${API_URL}/blogs/${id}`);
                if (response.data && response.data.blog) {
                    setBlog(response.data.blog);
                } else {
                    setError("Story not found in the archives.");
                }
            } catch (err) {
                console.error(err);
                setError("Failed to synchronize with the editorial stream.");
            } finally {
                setIsLoading(false);
            }
        };

        fetchBlog();
        window.scrollTo(0, 0);
    }, [id]);

    if (isLoading) {
        return (
            <div className="bg-white min-h-screen flex flex-col">
                <Header />
                <div className="flex-grow flex flex-col items-center justify-center gap-6">
                    <Loader2 size={64} className="animate-spin text-sky-200" strokeWidth={1} />
                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-300">Reconstructing Narrative...</p>
                </div>
                <Fotter />
            </div>
        );
    }

    if (error || !blog) {
        return (
            <div className="bg-white min-h-screen flex flex-col">
                <Header />
                <div className="flex-grow flex flex-col items-center justify-center gap-8 px-6 text-center">
                    <div className="w-24 h-24 bg-rose-50 rounded-full flex items-center justify-center text-rose-300 shadow-inner">
                        <Tag size={48} />
                    </div>
                    <div className="max-w-md">
                        <h2 className="text-3xl font-black text-sky-950 italic">Registry Silent</h2>
                        <p className="text-sm text-gray-400 font-bold mt-4 leading-relaxed">{error || "The requested story artifact has been moved or purged."}</p>
                        <Link to="/viewblog" className="mt-10 inline-flex items-center gap-3 px-10 py-5 bg-sky-950 text-white rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-sky-800 transition-all shadow-2xl shadow-sky-950/20 active:scale-95">
                            <ChevronLeft size={16} /> Return to Stream
                        </Link>
                    </div>
                </div>
                <Fotter />
            </div>
        );
    }

    return (
        <div className="bg-white selection:bg-sky-100 selection:text-sky-900 font-sans">
            <Header />

            {/* Hero Visual Area */}
            <section className="relative h-[70vh] w-full overflow-hidden pt-20">
                <div className="absolute inset-0">
                    <img
                        src={blog.coverImageUrl || "https://images.unsplash.com/photo-1544005313-94ddf0286df2?q=80&w=2000&auto=format&fit=crop"}
                        alt={blog.title}
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-sky-950/40 via-sky-950/10 to-white" />
                </div>

                <div className="relative z-10 h-full max-w-7xl mx-auto px-6 flex flex-col justify-end pb-32">
                    <Link to="/viewblog" className="inline-flex items-center gap-2 text-white font-black text-[10px] uppercase tracking-[0.3em] mb-12 hover:-translate-x-2 transition-transform drop-shadow-lg">
                        <ChevronLeft size={16} /> Back to Editorial Stream
                    </Link>

                    <div className="max-w-4xl">
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/20 backdrop-blur-xl border border-white/20 text-white text-[10px] font-black uppercase tracking-[0.2em] mb-8 shadow-2xl">
                            <Sparkles size={12} className="text-sky-300" />
                            Elite Discovery • {blog.category}
                        </div>
                        <h1 className="text-5xl md:text-7xl font-black text-white leading-[1.1] tracking-tighter drop-shadow-2xl mb-10">
                            {blog.title}
                        </h1>

                        <div className="flex flex-wrap items-center gap-8 text-[11px] font-black text-white uppercase tracking-widest opacity-90">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-2xl bg-sky-400 flex items-center justify-center text-sky-950 text-xs font-black shadow-lg">
                                    {blog.author.charAt(0)}
                                </div>
                                <span>{blog.author}</span>
                            </div>
                            <div className="w-1.5 h-1.5 rounded-full bg-white/30 hidden md:block" />
                            <div className="flex items-center gap-2">
                                <Calendar size={16} className="text-sky-300" />
                                {new Date(blog.createdAt || Date.now()).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                            </div>
                            <div className="w-1.5 h-1.5 rounded-full bg-white/30 hidden md:block" />
                            <div className="flex items-center gap-2">
                                <Clock size={16} className="text-sky-300" />
                                5 Minute Read
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Narrative Payload Section */}
            <section className="max-w-7xl mx-auto px-6 -mt-20 relative z-20 pb-40">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">

                    {/* Main Narrative Content */}
                    <div className="lg:col-span-8 bg-white rounded-[4rem] shadow-2xl border border-gray-50 p-12 md:p-20 overflow-hidden">

                        {/* Action Bar */}
                        <div className="flex items-center justify-between mb-16 border-b border-gray-50 pb-8">
                            <div className="flex items-center gap-4">
                                <button className="flex items-center gap-2 text-[10px] font-black text-sky-950 uppercase tracking-widest hover:text-sky-600 transition-colors">
                                    <MessageSquare size={16} /> 0 Responses
                                </button>
                                <div className="w-1 h-1 rounded-full bg-gray-200" />
                                <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center gap-2">
                                    <TrendingUp size={14} className="text-sky-300" /> High Impact
                                </span>
                            </div>
                            <div className="flex gap-4">
                                <button className="p-3 bg-gray-50 text-gray-400 rounded-2xl hover:bg-sky-50 hover:text-sky-600 transition-all">
                                    <Bookmark size={16} />
                                </button>
                                <button className="p-3 bg-gray-50 text-gray-400 rounded-2xl hover:bg-sky-50 hover:text-sky-600 transition-all">
                                    <Share2 size={16} />
                                </button>
                            </div>
                        </div>

                        {/* Story Text */}
                        <article className="prose prose-sky prose-xl max-w-none">
                            <div className="whitespace-pre-line text-gray-600 font-serif leading-[2.2] text-xl first-letter:text-6xl first-letter:font-black first-letter:text-sky-950 first-letter:mr-3 first-letter:float-left first-letter:mt-1 italic opacity-95">
                                {blog.content}
                            </div>
                        </article>

                        {/* Editorial Signature */}
                        <div className="mt-20 pt-16 border-t border-gray-100 flex flex-col md:flex-row items-center gap-10">
                            <div className="w-24 h-24 rounded-[2.5rem] bg-sky-950 flex items-center justify-center text-white text-3xl font-black shadow-2xl shadow-sky-950/20">
                                {blog.author.charAt(0)}
                            </div>
                            <div className="text-center md:text-left">
                                <p className="text-[10px] font-black text-sky-500 uppercase tracking-[0.3em] mb-2 text-center md:text-left uppercase">Elite Contributor</p>
                                <h4 className="text-2xl font-black text-sky-950 tracking-tight mb-4">{blog.author}</h4>
                                <p className="text-sm text-gray-400 font-bold max-w-md leading-relaxed">
                                    A dedicated travel chronicler for TravelAura Elite, uncovering secret rhythms and untold stories across the pearl of the Indian Ocean.
                                </p>
                                <div className="mt-6 flex justify-center md:justify-start gap-4">
                                    <button className="px-6 py-2.5 bg-sky-950 text-white rounded-xl text-[9px] font-black uppercase tracking-widest hover:bg-sky-800 transition-all">Follow Channel</button>
                                    <button className="px-6 py-2.5 bg-gray-50 text-sky-950 rounded-xl text-[9px] font-black uppercase tracking-widest hover:bg-white border border-transparent hover:border-gray-100 transition-all">Editorial Profile</button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Sidebar / Supplemental Discovery */}
                    <div className="lg:col-span-4 space-y-10">

                        {/* Trending Section */}
                        <div className="bg-sky-950 rounded-[3rem] p-10 text-white shadow-2xl shadow-sky-950/20 relative overflow-hidden group">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-sky-400/20 rounded-full blur-3xl -mr-16 -mt-16 group-hover:scale-150 transition-transform duration-1000" />
                            <h3 className="text-xs font-black uppercase tracking-[0.3em] mb-10 flex items-center gap-3">
                                <TrendingUp size={16} className="text-sky-400" /> Trending Discoveries
                            </h3>
                            <div className="space-y-10">
                                {[1, 2, 3].map(i => (
                                    <div key={i} className="group/item cursor-pointer">
                                        <span className="text-[9px] font-black text-sky-400 uppercase tracking-widest mb-2 block">Premium Travel</span>
                                        <h4 className="text-lg font-black tracking-tight leading-tight group-hover/item:text-sky-300 transition-colors mb-4">
                                            The Azure Secrets of the Southern Coastline
                                        </h4>
                                        <div className="flex items-center gap-3 text-[10px] font-bold text-sky-200/40 uppercase tracking-tighter">
                                            <Clock size={12} /> 4 MIN READ
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Newsletter Integration */}
                        <div className="bg-gradient-to-br from-white to-gray-50 border border-gray-100 rounded-[3rem] p-10 shadow-xl shadow-gray-200/50">
                            <span className="text-sky-600 font-black uppercase text-[9px] tracking-[0.3em] mb-6 block">Elite Stream Signup</span>
                            <h3 className="text-2xl font-black text-sky-950 tracking-tight leading-none mb-6">Never Miss a <span className="text-sky-600 italic">Story</span></h3>
                            <p className="text-xs text-gray-400 font-bold mb-10 leading-relaxed uppercase tracking-widest">
                                Weekly sensory reports and secret location artifacts delivered directly.
                            </p>
                            <div className="space-y-4">
                                <input
                                    type="email"
                                    placeholder="Access Token (Email)..."
                                    className="w-full bg-white border border-gray-100 rounded-2xl px-6 py-4 text-xs font-bold text-sky-950 outline-none focus:ring-4 focus:ring-sky-500/10 transition-all"
                                />
                                <button className="w-full py-5 bg-sky-950 text-white rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-xl shadow-sky-950/20 hover:bg-sky-800 transition-all flex items-center justify-center gap-3 active:scale-95">
                                    SUBSCRIBE <ArrowRight size={14} />
                                </button>
                            </div>
                        </div>

                        {/* Context Badge */}
                        <div className="flex items-center gap-4 p-8 bg-sky-50 rounded-[2.5rem] border border-sky-100 group">
                            <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center text-sky-600 shadow-sm group-hover:scale-110 transition-transform">
                                <Compass size={24} />
                            </div>
                            <div>
                                <p className="text-[10px] font-black text-sky-900/40 uppercase tracking-widest mb-1">Current Narrative</p>
                                <h4 className="text-xs font-black text-sky-950 tracking-tighter">{blog.category.toUpperCase()} EXPEDITION</h4>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <Fotter />

            {/* Narrative Specific Styles */}
            <style>{`
        .whitespace-pre-line { word-break: break-word; }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        .animate-fade-in { animation: fadeIn 1s ease-out forwards; }
      `}</style>
        </div>
    );
};

export default BlogDetail;
